if (!customElements.get('choice-set-picker')) {
  customElements.define(
    'choice-set-picker',
    class ChoiceSetPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.form = this.closest('form');
        if (!this.form) return;

        this.required = Number(this.dataset.required);
        this.cards = Array.from(this.querySelectorAll('[data-choice-card]'));
        this.status = this.querySelector('[data-choice-status]');
        this.summary = this.querySelector('[data-choice-summary]');
        this.properties = this.querySelector('[data-choice-properties]');
        this.configured = Number.isSafeInteger(this.required) && this.required > 0 &&
          Boolean(this.dataset.series?.trim()) && !this.querySelector('[data-choice-config-error]') &&
          this.cards.some((card) => card.dataset.available === 'true');
        this.quantities = new Map(this.cards.map((card) => [card.dataset.productId, 0]));
        this.addEventListener('click', this.handleClick);
        // Capture also protects Enter/requestSubmit before the theme's AJAX submit listener.
        this.form.addEventListener('submit', this.handleSubmit, true);
        this.form.addEventListener('formdata', this.handleFormData);
        this.initialized = true;
        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.form?.removeEventListener('submit', this.handleSubmit, true);
        this.form?.removeEventListener('formdata', this.handleFormData);
        this.initialized = false;
      }

      get total() {
        return Array.from(this.quantities?.values() || []).reduce((sum, quantity) => sum + quantity, 0);
      }

      get complete() {
        return this.configured && this.total === this.required;
      }

      handleClick = (event) => {
        const button = event.target.closest('[data-choice-delta]');
        if (!button || !this.contains(button) || button.disabled || !this.configured) return;
        const card = button.closest('[data-choice-card]');
        if (card.dataset.available !== 'true') return;
        const delta = Number(button.dataset.choiceDelta);
        const quantity = this.quantities.get(card.dataset.productId);
        if ((delta !== 1 && delta !== -1) || quantity + delta < 0 || this.total + delta > this.required) return;
        this.quantities.set(card.dataset.productId, quantity + delta);
        this.render();
      };

      handleSubmit = (event) => {
        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };

      validate() {
        if (!this.initialized || !this.complete) {
          this.status?.focus();
          return false;
        }
        this.writeProperties();
        return true;
      }

      // Use stable IDs and a canonical order: different combinations stay separate,
      // while selecting the same combination in a different order has the same properties.
      getProperties() {
        if (!this.complete) return {};
        const selected = this.cards.filter((card) => this.quantities.get(card.dataset.productId) > 0)
          .sort((a, b) => a.dataset.productId.localeCompare(b.dataset.productId, 'en'));
        const lines = selected.map((card) => this.describe(card));
        return {
          'セット内容': lines.join('\n'),
          'セット袋数': `${this.required}袋 / 1セット`,
          'シリーズ': this.dataset.series,
          ...(this.wrappingMethod ? { '_choice_set_wrapping': this.wrappingMethod } : {}),
          '_choice_set_version': '1',
          '_choice_set_scope': this.dataset.selectionScope || 'same_series',
          '_choice_set_items': JSON.stringify(selected.map((card) => ({
            product_id: card.dataset.productId,
            variant_id: card.dataset.variantId,
            quantity: this.quantities.get(card.dataset.productId),
          }))),
        };
      }

      get wrappingMethod() {
        const radio = this.closest('product-info')?.querySelector('variant-selects input[data-option-name="包装方法"]:checked');
        return radio?.value || this.dataset.wrapping || '';
      }

      describe(card) {
        const flavor = card.dataset.flavor ? `（${card.dataset.flavor}）` : '';
        return `${card.dataset.title}${flavor} × ${this.quantities.get(card.dataset.productId)}`;
      }

      writeProperties() {
        this.properties.replaceChildren();
        Object.entries(this.getProperties()).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          this.properties.append(input);
        });
      }

      handleFormData = (event) => {
        // Rebuild from component state, never from editable summary markup.
        Object.entries(this.getProperties()).forEach(([key, value]) => {
          event.formData.set(`properties[${key}]`, value);
        });
      };

      syncSubmitButton() {
        const productForm = this.closest('product-form');
        const button = this.form.querySelector('[name="add"]');
        if (!button) return;
        const variantDisabled = productForm?.choiceSetVariantDisabled ?? button.dataset.choiceSetUnavailable === 'true';
        button.disabled = variantDisabled || !this.complete;
      }

      render() {
        const total = this.total;
        this.cards.forEach((card) => {
          const quantity = this.quantities.get(card.dataset.productId);
          const unavailable = !this.configured || card.dataset.available !== 'true';
          card.dataset.quantity = String(quantity);
          card.toggleAttribute('data-selected', quantity > 0);
          card.querySelector('[data-choice-quantity]').textContent = quantity;
          card.querySelector('[data-choice-delta="-1"]').disabled = unavailable || quantity === 0;
          card.querySelector('[data-choice-delta="1"]').disabled = unavailable || total >= this.required;
        });
        this.status.textContent = !this.configured
          ? '現在、このセットは選択を受け付けていません。'
          : `${total} / ${this.required}袋 · ${this.complete ? '選択完了' : `あと${this.required - total}袋選んでください`}`;
        this.summary.replaceChildren();
        const selected = this.cards.filter((card) => this.quantities.get(card.dataset.productId) > 0);
        (selected.length ? selected : [null]).forEach((card) => {
          const item = document.createElement('li');
          item.textContent = card ? this.describe(card) : 'まだ選択されていません。';
          this.summary.append(item);
        });
        this.writeProperties();
        this.syncSubmitButton();
      }
    }
  );
}
